import { cn } from '../utils'

/**
 * Every icon name the ICE icon font exposes, in source order. Exported so
 * consumers can enumerate the set — the UI docs gallery renders straight from
 * this array rather than keeping a second copy that silently drifts.
 *
 * `IconName` is derived from it, so adding a name here is all that is needed.
 */
export const iconNames = [
  'add',
  'add-user-filed',
  'arrow-left',
  'arrow-merge',
  'arrow-right',
  'arrow-split',
  'books',
  'calendar',
  'camera',
  'chart-table',
  'checkmark',
  'chevron-down',
  'chevron-left',
  'chevron-right',
  'chevron-up',
  'client',
  'clock',
  'close-delete-x',
  'cloud',
  'cog',
  'cog-filled',
  'column-triple-edit',
  'comment-conversation',
  'compare-documents',
  'confirmation-checkmark',
  'confirmation-checkmark-filled',
  'convert',
  'copy-file',
  'copy-to-clipboard',
  'cursor-move',
  'dashboard-filled',
  'disable',
  'doc-basic',
  'doc-text',
  'doc-recent',
  'download',
  'edit',
  'edit-filled',
  'edit-highlight',
  'edit-highlight-filled',
  'edit-file',
  'edit-file-filled',
  'edit-user-filled',
  'enable',
  'email',
  'email-filled',
  'email-send-attachment',
  'error',
  'error-filled',
  'error-exclamation-filled',
  'export',
  'eye',
  'eye-filled',
  'eye-crossed-out-filled',
  'file-doc',
  'file-excel',
  'file-pdf',
  'file-ppt',
  'filter',
  'folder',
  'imanage-bug',
  'import',
  'info',
  'info-filled',
  'indent',
  'keyboard-shortcuts',
  'kebab-vertical',
  'link-askew-linked',
  'link-askew-unlinked',
  'list-preview-hide',
  'list-preview-show',
  'lock-closed',
  'map-pin-filled',
  'matter',
  'mobility-poilcy',
  'number',
  'one-note-file',
  'open',
  'outdent',
  'plus',
  'print-filled',
  'purge-permanently-delete',
  'question-mark-circle',
  'redo',
  'refresh',
  'magnify-plus',
  'magnify-minus',
  'magnifying-glass',
  'replace-file',
  'reorder',
  'restore',
  'send',
  'scissors',
  'screen',
  'scroll-down',
  'scroll-up',
  'search',
  'sent-mail',
  'shortcut',
  'sign-here',
  'sparkles',
  'sparkles-filled',
  'stamp-selected',
  'string',
  'tag-filled',
  'text-file',
  'text-lines',
  'thumbs-up',
  'ticket',
  'timeline',
  'timeline-filled',
  'trash',
  'triangle-down',
  'triangle-up',
  'undo',
  'upload',
  'user',
  'user-circle-filled',
  'user-group-3-alt',
  'user-group-3-filled',
  'user-shield-filled',
  'user-with-lock',
  'warning',
  'warning-filled',
  'whats-new',
  'notification',
] as const

export type IconName = (typeof iconNames)[number]

type IconProps = {
  name: IconName
  className?: string
  ariaLabel?: string
  role?: string
}

/**
 * A list of all icons can be found [here](https://clofo-public-assets.s3.us-east-1.amazonaws.com/icons/frontend/index.html)
 *
 */
export function IceIcon({ name, className, ariaLabel, role = 'img' }: IconProps) {
  const isDecorative = !ariaLabel

  return (
    <i
      className={cn(`text-2xl im-icon--${name} pointer-events-none`, className)}
      aria-hidden={isDecorative ? true : undefined}
      aria-label={ariaLabel}
      role={isDecorative ? undefined : role}
    />
  )
}
